import UserLessons from "/js/core/user-lessons";

/**
 * Simple "value object" reprenting the user's data for a given lesson.
 * Its data maps to the "UserLesson" database record.
 */
export default class UserLesson {

  #lessonId = null;
  #index = -1;
  #totalCards = 0;
  #isComplete = false;

  #startedAt = 0;
  #lastAccessedAt = 0;
  #completedAt = 0;

  #answers = null;

  // Not used by web client but need to preserve this info as it goes to and from the server:
  #pinnedCardObects = null;
  #sentiment = null;

  constructor(lessonId, totalCards) {
    this.#lessonId = lessonId;
    this.#startedAt = new Date().getTime();
    this.#totalCards = totalCards;
    this.touch();
  }

  get lessonId() { return this.#lessonId; }

  get index() { return this.#index; }
  set index(i) {
    if (this.#index > i) {
      return;
    }
    this.#index = i;
  }

  get totalCards() { return this.#totalCards; }

  get isComplete() { return this.#isComplete; }
  setToComplete() {
    this.#isComplete = true;
    this.#completedAt = new Date().getTime();
    this.index = this.totalCards;  // Note, =/= total - 1
  }

  get startedAt() { return this.#startedAt; }
  get completedAt() { return this.#completedAt; }
  get lastAccessedAt() { return this.#lastAccessedAt; }

  get answers() {
    return this.#answers;
  }
  set answers(o) {
    this.#answers = o;
  }

  get sentiment() { return this.#sentiment; }
  set sentiment(o) { this.#sentiment = o; }

  touch() {
    this.#lastAccessedAt = new Date().getTime();
  }

  /**
   * Returns a Number percentage if lesson is in progress (started but not completed). Else NaN.
   */
  get inProgressPercent() {
    if (this.isComplete) {
      return NaN;
    }
    const pct = this.index / this.totalCards;
    if (isNaN(pct)) {
      return NaN;
    }
    return pct;
  }

  /**
   * Makes data that's fit for consumption on the server-side.
   * Is also used for storing the data locally.
   */
  asServerObject() {
    const o = {
      lesson_id: this.#lessonId,
      started_at: this.#startedAt,
      last_accessed: this.#lastAccessedAt,
      is_completed: this.#isComplete
    };

    // `progress` is a json string, not an object:
    o.progress = JSON.stringify({ current: this.#index, total: this.#totalCards });

    // Only add fields for values that exist:

    // `answers` (`output`) is also a json string, and also, is wrapped in an array.
    if (this.answers) {
      o.output = JSON.stringify([this.answers]);
    }
    if (this.#completedAt > 0) {
      o.completed_at = this.#completedAt;
    }
    if (this.#pinnedCardObects) {
      o.pinned_card_objects = this.#pinnedCardObects;
    }
    if (this.#sentiment) {
      o.sentiment = this.#sentiment;
    }
    return o;
  }

  /**
   *
   */
  static fromServerObject(o) {
    if (!o.lesson_id || !o.progress) {
      clw("WARNING: Missing required property lesson_id or progress", o);
      return null;
    }

    // Parse the o.progress json string:
    let progressObject;
    try {
      progressObject = JSON.parse(o.progress);
    } catch (e) {
      clw("WARNING: Progress string didn't parse", e, o.progress, o);
      return null;
    }
    if (isNaN(progressObject.current)) {
      clw("WARNING: Progress object has no property 'current'", progressObject, o.progress, o);
      return null;
    }
    const userLesson = new UserLesson(o.lesson_id, progressObject.total);

    userLesson.#index = progressObject.current;

    userLesson.#startedAt = !isNaN(o.started_at) ? o.started_at : 0;

    userLesson.#lastAccessedAt = !isNaN(o.last_accessed) ? o.last_accessed : 0;

    userLesson.#isComplete = !!o.is_completed; // (nb, this does come in as a boolean)

    // These fields shd only be set if they exist IMO:

    if (!isNaN(o.completed_at)) {
      userLesson.#completedAt = o.completed_at;
    }

    if (o.output !== undefined) {
      // Parse the o.output string:
      let array;
      try {
        array = JSON.parse(o.output);
        if (!Array.isArray(array)) {
          clw("WARNING: 'output' is not an array", o.output, o);
        }
        if (array.length > 0) {
          userLesson.#answers = array[0];
          // cl('added answers:', array[0]);
        }
      } catch (e) {
        clw("WARNING: Couldn't parse 'output' string", o.output, o);
      }
    }
    if (o.pinned_card_objects !== undefined) {
      userLesson.#pinnedCardObects = o.pinned_card_objects;
    }

    if (o.sentiment !== undefined) {
      userLesson.#sentiment = o.sentiment;
    }

    return userLesson;
  }
}
